/**
 * Holds the browser back while something is still being uploaded.
 *
 * End Meeting navigates away and then finishes the audio upload in the
 * background, so the few seconds after it are exactly when closing the tab
 * loses the recording — there is no `sendBeacon` fallback and the request is
 * simply abandoned. A `beforeunload` listener cannot finish the upload, but it
 * does make the browser ask first, which is the difference between losing a
 * meeting and waiting two seconds.
 *
 * Counted rather than boolean: two meetings can be settling at once, and the
 * second one releasing must not take the guard off the first.
 */
let pending = 0

function askBeforeLeaving(event: BeforeUnloadEvent): void {
  event.preventDefault()
  // Browsers show their own wording and ignore ours, but Chrome still needs
  // `returnValue` set for the dialog to appear at all.
  event.returnValue = ''
}

/**
 * Start guarding, and hand back the release.
 *
 * A returned release rather than a matching `end()` call: the count must not
 * drift, and a release that runs twice — a `finally` plus an error path, say —
 * would otherwise lift the guard while an upload is still running.
 *
 * @returns Idempotent release; calling it more than once does nothing.
 * @example const release = guardPendingUpload(); try { await upload() } finally { release() }
 */
export function guardPendingUpload(): () => void {
  pending += 1
  if (pending === 1) window.addEventListener('beforeunload', askBeforeLeaving)

  let released = false
  return () => {
    if (released) return
    released = true
    pending -= 1
    if (pending === 0) window.removeEventListener('beforeunload', askBeforeLeaving)
  }
}

/** How many uploads are holding the guard; for tests and for debugging. */
export function pendingUploadCount(): number {
  return pending
}
